import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSelector } from 'react-redux';

import { useGetFiles } from '@widgets/Files/hooks/useGetFiles';
import { useGetFoldersAndFilesFromParsedData } from '@widgets/Files/hooks/useGetFoldersAndFilesFromParsedData';
import { IFIleEntity } from '@widgets/Files/model/types/fileEntity.interface';
import { TFileId } from '@widgets/Files/model/types/fileId.type';

import { useAppSelector } from '../../../../store';
import { filesSelector } from '../../../../store/slices/filesSlice/filesSlice';

export const useDirectionModal = (
    ref: React.RefObject<ReactNode>,
    currentFileId: TFileId,
    parsedFilesData: IFIleEntity[],
    onChangeCurrentFile: (fileId: TFileId, fileInfo: IFIleEntity) => void
) => {
    const { files } = useGetFoldersAndFilesFromParsedData({ parsedFilesData });
    const ids = useMemo(() => files.map((file) => file.id), [files]);
    let isLast = false;
    let isFirst = false;

    if (currentFileId === ids[ids.length - 1]) {
        isLast = true;
    }
    if (currentFileId === ids[0]) {
        isFirst = true;
    }

    const onClickNext = useCallback(() => {
        if (currentFileId === ids[ids.length - 1]) return;

        const newFileIndex = ids.indexOf(currentFileId) + 1;
        const newFileId = ids[newFileIndex];

        const newFileInfo = parsedFilesData.find((f) => f.id === newFileId)!;

        onChangeCurrentFile(newFileId, newFileInfo);
    }, [currentFileId, ids, parsedFilesData, onChangeCurrentFile]);

    const onClickPrev = useCallback(() => {
        if (currentFileId === ids[0]) return;

        const newFileIndex = ids.indexOf(currentFileId) - 1;
        const newFileId = ids[newFileIndex];
        const newFileInfo = parsedFilesData.find((f) => f.id === newFileId)!;

        onChangeCurrentFile(newFileId, newFileInfo);
    }, [currentFileId, ids, parsedFilesData, onChangeCurrentFile]);

    const currentRef = useRef(ref);

    useEffect(() => {
        const moveDirectionFile = (evt: KeyboardEvent) => {
            if (evt.key === 'ArrowLeft') {
                onClickPrev();
            }
            if (evt.key === 'ArrowRight') {
                onClickNext();
            }
        };

        if (currentRef && currentRef.current) {
            window.addEventListener('keydown', moveDirectionFile);
        }

        return () => {
            window.removeEventListener('keydown', moveDirectionFile);
        };
    }, [onClickNext, onClickPrev]);

    return { isLast, isFirst, onClickPrev, onClickNext };
};
